import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [prices, setPrices] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdmin, setShowAdmin] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [autoExecutionEnabled, setAutoExecutionEnabled] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [emailTestLoading, setEmailTestLoading] = useState(false);

  useEffect(() => {
    const initDashboard = async () => {
      try {
        setLoading(true);
        
        // Fetch admin data using stored access token
        const storedAccessToken = localStorage.getItem('accessToken');
        if (storedAccessToken) {
          await fetchAdminData(storedAccessToken);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    initDashboard();
    fetchCurrentPrices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  const fetchAdminData = async (token) => {
    try {
      const headers = { 'x-access-token': token };
      const [statsResponse, ordersResponse, autoExecutionResponse] = await Promise.all([
        axios.get('/api/admin/stats', { headers }),
        axios.get('/api/admin/orders?limit=10', { headers }),
        axios.get('/api/admin/auto-execution', { headers })
      ]);

      setStats(statsResponse.data);
      setRecentOrders(ordersResponse.data);
      setAutoExecutionEnabled(autoExecutionResponse.data.auto_execution_enabled);
      setShowAdmin(true);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setShowAdmin(false);
      // Clear invalid token
      localStorage.removeItem('accessToken');
    }
  };

  const fetchCurrentPrices = async () => {
    try {
      const response = await axios.get('/api/prices/current');
      setPrices(response.data);
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  const refreshPricesFromWebsite = async () => {
    try {
      // Show loading state
      const refreshBtn = document.querySelector('.refresh-btn');
      const originalText = refreshBtn.textContent;
      refreshBtn.textContent = '🔄 Updating...';
      refreshBtn.disabled = true;

      // Call the refresh endpoint to get fresh prices from zlatarenta.com
      const response = await axios.post('/api/prices/refresh');
      
      if (response.data.success) {
        // Update the prices state with fresh data
        setPrices(response.data.prices);
        
        // Show success feedback
        refreshBtn.textContent = '✅ Updated!';
        setTimeout(() => {
          refreshBtn.textContent = originalText;
          refreshBtn.disabled = false;
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Failed to refresh prices');
      }
    } catch (error) {
      console.error('Error refreshing prices:', error);
      
      // Show error feedback
      const refreshBtn = document.querySelector('.refresh-btn');
      refreshBtn.textContent = '❌ Error';
      setTimeout(() => {
        refreshBtn.textContent = '🔄 Refresh Prices';
        refreshBtn.disabled = false;
      }, 2000);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price);
  };

  const formatDate = (dateString) => {
    const cetTime = new Date(dateString).toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    return `${cetTime} CET`;
  };

  const formatDateCompact = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'executed': return '#007bff';
      case 'expired': return '#ffc107';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  const getOrderTypeLabel = (type) => {
    return type === 'buy' ? 'NAKUP' : 'ODKUP';
  };

  const toggleAutoExecution = async () => {
    setToggleLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'x-access-token': token };
      
      const response = await axios.post('/api/admin/auto-execution/toggle', 
        { enabled: !autoExecutionEnabled }, 
        { headers }
      );
      
      setAutoExecutionEnabled(response.data.auto_execution_enabled);
    } catch (error) {
      console.error('Error toggling auto execution:', error);
    } finally {
      setToggleLoading(false);
    }
  };

  const testEmail = async (emailType) => {
    setEmailTestLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'x-access-token': token };
      
      const response = await axios.post('/api/admin/test-email', {
        email_type: emailType,
        test_email: 'katja.damij@tadadviser.com' // Change this to your test email
      }, { headers });
      
      alert(`✅ ${response.data.message}`);
    } catch (error) {
      console.error('Error sending test email:', error);
      alert(`❌ Failed to send test email: ${error.response?.data?.message || error.message}`);
    } finally {
      setEmailTestLoading(false);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to permanently delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'x-access-token': token };
      
      const response = await axios.delete(`/api/admin/orders/${orderId}`, { headers });
      
      alert(`✅ ${response.data.message}`);
      
      // Refresh the recent orders list
      await fetchAdminData(token);
      
      // Close modal if the deleted order was selected
      if (selectedOrder && selectedOrder.id === orderId) {
        closeModal();
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      alert(`❌ Failed to delete order: ${error.response?.data?.message || error.message}`);
    }
  };

  const cancelOrder = async (orderId, reason = 'Cancelled by admin') => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'x-access-token': token };
      
      const response = await axios.post(`/api/admin/orders/${orderId}/cancel`, 
        { reason }, 
        { headers }
      );
      
      alert(`✅ ${response.data.message}`);
      
      // Refresh the recent orders list
      await fetchAdminData(token);
      
      // Update the selected order if it's the one we just cancelled
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: 'cancelled' });
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert(`❌ Failed to cancel order: ${error.response?.data?.message || error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Market Overview</h2>
        <button onClick={refreshPricesFromWebsite} className="refresh-btn">
          🔄 Refresh Prices
        </button>
      </div>

      {/* Current Prices */}
      <div className="prices-section">
        <h3>Current Precious Metal Prices (1g)</h3>
        <div className="prices-grid">
          {Object.entries(prices)
            .filter(([product]) => {
              // Only show 1g precious metals
              const supportedProducts = ['gold-1g', 'silver-1g', 'palladium-1g', 'platinum-1g'];
              return supportedProducts.includes(product);
            })
            .map(([product, data]) => {
              // Format product name for display
              const metalName = product.replace('-1g', '').charAt(0).toUpperCase() + product.replace('-1g', '').slice(1);
              return (
                <div key={product} className="price-card" data-metal={metalName.toLowerCase()}>
                  <h4>{metalName} 1G</h4>
                  <div className="price">{formatPrice(data.price)}</div>
                  <div className="timestamp">
                    Updated: {formatDate(data.timestamp)}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Admin Dashboard */}
      <div className="admin-section">
        {showAdmin && (
          <div className="admin-dashboard">
            <div className="admin-header">
              <h3>Admin Dashboard</h3>
              <div className="auto-execution-toggle">
                <label className="toggle-label">
                  <span className={`toggle-text ${!autoExecutionEnabled ? 'disabled' : ''}`}>
                    Auto Execution: {autoExecutionEnabled ? 'ON' : 'OFF'}
                  </span>
                  <div className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={autoExecutionEnabled} 
                      onChange={toggleAutoExecution}
                      disabled={toggleLoading}
                    />
                    <span className="slider"></span>
                  </div>
                </label>
                {toggleLoading && <div className="toggle-loading">Updating...</div>}
              </div>
            </div>

            {/* Email Testing Section */}
            <div className="email-test-section">
              <h4>📧 Test Email System</h4>
              <div className="email-test-buttons">
                <button 
                  onClick={() => testEmail('expiration')} 
                  disabled={emailTestLoading}
                  className="test-btn expiration"
                >
                  {emailTestLoading ? 'Sending...' : 'Test Expiration Email'}
                </button>
                <button 
                  onClick={() => testEmail('execution')} 
                  disabled={emailTestLoading}
                  className="test-btn execution"
                >
                  {emailTestLoading ? 'Sending...' : 'Test Execution Email'}
                </button>
                <button 
                  onClick={() => testEmail('general')} 
                  disabled={emailTestLoading}
                  className="test-btn general"
                >
                  {emailTestLoading ? 'Sending...' : 'Test General Email'}
                </button>
              </div>
              <small>Test emails will be sent to: katja.damij@tadadviser.com</small>
            </div>

            {/* Statistics */}
            {stats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-value">{stats.totalOrders}</div>
                  <div className="stat-label">Total Orders</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🟢</div>
                  <div className="stat-value">{stats.activeOrders}</div>
                  <div className="stat-label">Active Orders</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">✅</div>
                  <div className="stat-value">{stats.executedOrders}</div>
                  <div className="stat-label">Executed Orders</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">⏰</div>
                  <div className="stat-value">{stats.expiredOrders}</div>
                  <div className="stat-label">Expired Orders</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">❌</div>
                  <div className="stat-value">{stats.cancelledOrders}</div>
                  <div className="stat-label">Cancelled Orders</div>
                </div>
              </div>
            )}

            {/* Recent Orders */}
            <div className="recent-orders">
              <h4>Recent Orders</h4>
              <div className="orders-table">
                <div className="table-header">
                  <div>ID</div>
                  <div>Customer</div>
                  <div>Product</div>
                  <div>Metal</div>
                  <div>Type</div>
                  <div>Target Spot Price</div>
                  <div>Status</div>
                  <div>Created</div>
                </div>
                {recentOrders.map(order => (
                  <div 
                    key={order.id} 
                    className="table-row clickable-row" 
                    onClick={() => handleOrderClick(order)}
                    title="Click to view details"
                  >
                    <div>{order.id}</div>
                    <div>{order.customer_email}</div>
                    <div>{order.product_name}</div>
                    <div>
                      {order.metal_type ? (
                        <span 
                          className="metal-badge" 
                          data-metal={order.metal_type.toLowerCase()}
                        >
                          {order.metal_type.charAt(0).toUpperCase() + order.metal_type.slice(1)}
                        </span>
                      ) : (
                        <span className="metal-badge unknown">Auto</span>
                      )}
                    </div>
                    <div className={`order-type ${order.order_type}`}>
                      {order.order_type.toUpperCase()}
                    </div>
                    <div>{formatPrice(order.target_price)}</div>
                    <div 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </div>
                    <div className="date-compact">{formatDateCompact(order.created_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Getting Started Section */}
      <div className="getting-started">
        <h3>Getting Started</h3>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Create a Limit Order</h4>
              <p>Set your target spot price and duration for 1g precious metals (Gold, Silver, Palladium, Platinum)</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Automated Monitoring</h4>
              <p>Our system checks prices every 5 minutes and executes when targets are met</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Instant Notifications</h4>
              <p>Get email alerts when orders are executed or expired</p>
            </div>
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Details #{selectedOrder.id}</h3>
              <div className="modal-actions">
                {showAdmin && (
                  <>
                    {selectedOrder.status === 'active' && (
                      <button 
                        className="cancel-order-btn"
                        onClick={() => cancelOrder(selectedOrder.id)}
                        title="Cancel this order"
                      >
                        🚫 Cancel
                      </button>
                    )}
                    <button 
                      className="delete-order-btn"
                      onClick={() => deleteOrder(selectedOrder.id)}
                      title="Permanently delete this order"
                    >
                      🗑️ Delete
                    </button>
                  </>
                )}
                <button className="modal-close" onClick={closeModal}>
                  ✕
                </button>
              </div>
            </div>
            
            <div className="modal-body">
              <div className="order-details-grid">
                <div className="detail-group">
                  <h4>Order Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Order ID:</span>
                    <span className="detail-value">#{selectedOrder.id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span 
                      className="detail-value status-badge"
                      style={{ backgroundColor: getStatusColor(selectedOrder.status) }}
                    >
                      {selectedOrder.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type:</span>
                    <span className={`detail-value order-type ${selectedOrder.order_type}`}>
                      {getOrderTypeLabel(selectedOrder.order_type)}
                    </span>
                  </div>
                </div>

                <div className="detail-group">
                  <h4>Product & Pricing</h4>
                  <div className="detail-row">
                    <span className="detail-label">Product:</span>
                    <span className="detail-value">{selectedOrder.product_name}</span>
                  </div>
                  {selectedOrder.metal_type && (
                    <div className="detail-row">
                      <span className="detail-label">Metal Type:</span>
                      <span 
                        className="detail-value metal-type" 
                        data-metal={selectedOrder.metal_type.toLowerCase()}
                      >
                        {selectedOrder.metal_type.charAt(0).toUpperCase() + selectedOrder.metal_type.slice(1)}
                      </span>
                    </div>
                  )}
                  {selectedOrder.product_weight && (
                    <div className="detail-row">
                      <span className="detail-label">Weight:</span>
                      <span className="detail-value">{selectedOrder.product_weight}g</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Quantity:</span>
                    <span className="detail-value">{selectedOrder.quantity || 1} piece{(selectedOrder.quantity || 1) > 1 ? 's' : ''}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Target Spot Price:</span>
                    <span className="detail-value price">{formatPrice(selectedOrder.target_price)} per gram</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Duration:</span>
                    <span className="detail-value">{selectedOrder.duration_days} days</span>
                  </div>
                </div>

                <div className="detail-group">
                  <h4>Customer Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedOrder.customer_email}</span>
                  </div>
                  {selectedOrder.phone && (
                    <div className="detail-row">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{selectedOrder.phone}</span>
                    </div>
                  )}
                </div>

                {/* Customer Verification Information */}
                {(selectedOrder.id_type || selectedOrder.tax_number || selectedOrder.emso) && (
                  <div className="detail-group">
                    <h4>ID Document</h4>
                    {selectedOrder.id_type && (
                      <div className="detail-row">
                        <span className="detail-label">Document Type:</span>
                        <span className="detail-value">{selectedOrder.id_type === 'osebna' ? 'Personal ID' : 'Passport'}</span>
                      </div>
                    )}
                    {selectedOrder.id_number && (
                      <div className="detail-row">
                        <span className="detail-label">Document Number:</span>
                        <span className="detail-value">{selectedOrder.id_number}</span>
                      </div>
                    )}
                    {selectedOrder.id_issue_date && (
                      <div className="detail-row">
                        <span className="detail-label">Issue Date:</span>
                        <span className="detail-value">{formatDate(selectedOrder.id_issue_date)}</span>
                      </div>
                    )}
                    {selectedOrder.id_expiry_date && (
                      <div className="detail-row">
                        <span className="detail-label">Expiry Date:</span>
                        <span className="detail-value">{formatDate(selectedOrder.id_expiry_date)}</span>
                      </div>
                    )}
                  </div>
                )}

                {(selectedOrder.tax_number || selectedOrder.emso || selectedOrder.birthplace) && (
                  <div className="detail-group">
                    <h4>Personal Data</h4>
                    {selectedOrder.tax_number && (
                      <div className="detail-row">
                        <span className="detail-label">Tax Number:</span>
                        <span className="detail-value">{selectedOrder.tax_number}</span>
                      </div>
                    )}
                    {selectedOrder.emso && (
                      <div className="detail-row">
                        <span className="detail-label">EMŠO:</span>
                        <span className="detail-value">{selectedOrder.emso}</span>
                      </div>
                    )}
                    {selectedOrder.birthplace && (
                      <div className="detail-row">
                        <span className="detail-label">Place of Birth:</span>
                        <span className="detail-value">{selectedOrder.birthplace}</span>
                      </div>
                    )}
                  </div>
                )}

                {(selectedOrder.dual_citizenship || selectedOrder.sanctions_connection || selectedOrder.pep_status) && (
                  <div className="detail-group">
                    <h4>Verification Status</h4>
                    {selectedOrder.dual_citizenship && (
                      <div className="detail-row">
                        <span className="detail-label">Dual Citizenship:</span>
                        <span className={`detail-value ${selectedOrder.dual_citizenship === 'yes' ? 'warning' : 'success'}`}>
                          {selectedOrder.dual_citizenship === 'yes' ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                    {selectedOrder.sanctions_connection && (
                      <div className="detail-row">
                        <span className="detail-label">Sanctions Connection:</span>
                        <span className={`detail-value ${selectedOrder.sanctions_connection === 'yes' ? 'warning' : 'success'}`}>
                          {selectedOrder.sanctions_connection === 'yes' ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                    {selectedOrder.pep_status && (
                      <div className="detail-row">
                        <span className="detail-label">PEP Status:</span>
                        <span className={`detail-value ${selectedOrder.pep_status === 'yes' ? 'warning' : 'success'}`}>
                          {selectedOrder.pep_status === 'yes' ? 'Yes (PEP)' : 'No (Non-PEP)'}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {selectedOrder.source_of_funds && (
                  <div className="detail-group">
                    <h4>Source of Funds</h4>
                    <div className="detail-row">
                      <span className="detail-label">Sources:</span>
                      <span className="detail-value">
                        {selectedOrder.source_of_funds.split(',').map(source => {
                          const sourceLabels = {
                            'prihranki': 'Savings',
                            'placa': 'Salary',
                            'pokojnina': 'Pension',
                            'studentsko_delo': 'Student Work',
                            'honorar': 'Fee',
                            'stipendija': 'Scholarship',
                            'regres': 'Allowance',
                            'posel': 'Business',
                            'prodaja_nepremicnine': 'Real Estate Sale',
                            'oddaja_nepremicnine': 'Real Estate Rental',
                            'prodaja_vozila': 'Vehicle Sale',
                            'vrednostni_papirji': 'Securities',
                            'kripto': 'Crypto',
                            'varcevanje_zavarovanje': 'Savings/Insurance',
                            'prodaja_zlata_kovin': 'Gold/Metal Sale',
                            'ostalo': 'Other'
                          };
                          return sourceLabels[source.trim()] || source.trim();
                        }).join(', ')}
                      </span>
                    </div>
                    {selectedOrder.custom_source_description && (
                      <div className="detail-row">
                        <span className="detail-label">Custom Source:</span>
                        <span className="detail-value">{selectedOrder.custom_source_description}</span>
                      </div>
                    )}
                  </div>
                )}

                {selectedOrder.recommendation_from && (
                  <div className="detail-group">
                    <h4>Additional Information</h4>
                    <div className="detail-row">
                      <span className="detail-label">Recommended By:</span>
                      <span className="detail-value">{selectedOrder.recommendation_from}</span>
                    </div>
                  </div>
                )}

                <div className="detail-group">
                  <h4>Timeline</h4>
                  <div className="detail-row">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">{formatDate(selectedOrder.created_at)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Expires:</span>
                    <span className="detail-value">{formatDate(selectedOrder.expires_at)}</span>
                  </div>
                  {selectedOrder.executed_at && (
                    <div className="detail-row">
                      <span className="detail-label">Executed:</span>
                      <span className="detail-value">{formatDate(selectedOrder.executed_at)}</span>
                    </div>
                  )}
                </div>

                {selectedOrder.product_image && (
                  <div className="detail-group full-width">
                    <h4>Product Image</h4>
                    <div className="detail-row">
                      <div className="product-image-container">
                        <img 
                          src={selectedOrder.product_image} 
                          alt={`${selectedOrder.product_name}`}
                          className="product-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'block';
                          }}
                        />
                        <div className="image-error" style={{display: 'none'}}>
                          Image not available
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedOrder.notes && (
                  <div className="detail-group full-width">
                    <h4>Notes</h4>
                    <div className="detail-row">
                      <span className="detail-value notes">{selectedOrder.notes}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;